import { Paper } from "helpers/api";
import Citation, { CitationProps } from "./Citation";

/**
 * CitationAdapter is a wrapper component that adapts the prop structure from the remark
 * citation plugins to the Citation component's expected prop interface.
 *
 * The adapter handles two citation formats:
 * - Legacy [1] citations: Uses paperIndex to find the paper
 * - Modern <paper_cite> tags: Uses paper_id to find the paper and calculate the display index
 *
 * This separation allows the Citation component to remain focused on rendering while
 * this adapter handles the prop mapping logic.
 */
interface CitationAdapterProps
  extends Omit<CitationProps, "paper" | "lastInGroup" | "hasUsedFullText"> {
  papers: Paper[];
  paper_id?: string;
  fullTextPaperIds?: string[];
}

export const CitationAdapter = ({
  papers,
  paperIndex,
  paper_id,
  quote,
  quoteIndex,
  fullTextPaperIds,
  ...otherProps
}: CitationAdapterProps) => {
  // Map paper_id to paper and correct display number
  let paper = undefined;
  let displayPaperIndex: number | undefined = paperIndex;

  if (paper_id) {
    // Find paper by paper_id (primary method for <paper_cite> tags)
    paper = papers.find((p) => p.paper_id === paper_id);
    // Calculate paperIndex from paper position in array
    const foundIndex = papers.findIndex((p) => p.paper_id === paper_id);
    if (foundIndex !== -1) {
      displayPaperIndex = foundIndex;
    } else {
      // Paper not found by paper_id
      return null;
    }
  } else if (paperIndex !== undefined && papers[paperIndex]) {
    // Fallback: use paperIndex as 0-based index (for [1] citations converted to paper_cite)
    paper = papers[paperIndex];
    displayPaperIndex = paperIndex;
  }

  // If no valid paper or index found, don't render the citation.
  // This could happen if, say, the model outputs an invalid paper_id.
  if (!paper || displayPaperIndex === undefined) {
    return null;
  }

  const fullTextPaperIdsSet = new Set(fullTextPaperIds);

  const hasUsedFullText = !!(
    paper?.paper_id && fullTextPaperIdsSet.has(paper.paper_id)
  );

  return (
    <Citation
      {...otherProps}
      paper={paper}
      paperIndex={displayPaperIndex}
      quote={quote}
      quoteIndex={quoteIndex}
      lastInGroup
      hasUsedFullText={hasUsedFullText}
    />
  );
};
